import './authmodal.css';
import { t, tText } from './i18n';

/**
 * 일반 로그인/회원가입: a bare login+signup form, nothing else — no room
 * list, no social buttons. Reached from the main menu's "일반 로그인 /
 * 회원가입" button, next to (not instead of) the Kakao one-click button.
 *
 * One modal, two tabs: 로그인 (id/pw + a forgot-password hint — there's no
 * real reset flow yet, just a pointer to ask an admin) and 회원가입
 * (id/pw/pw-confirm/email).
 */
export interface AuthModalCallbacks {
  onLogin(name: string, password: string): void;
  onSignup(name: string, password: string, email: string): void;
  onClose(): void;
}

type Mode = 'login' | 'signup';

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  return e;
}

export class AuthModal {
  private readonly overlay = el('div', 'authmodal-overlay ui-scrim');
  private readonly modal = el('div', 'authmodal-modal interactive');
  private readonly tabLogin = el('button', 'authmodal-tab');
  private readonly tabSignup = el('button', 'authmodal-tab');
  private readonly user = el('input', 'authmodal-input') as HTMLInputElement;
  private readonly pass = el('input', 'authmodal-input') as HTMLInputElement;
  private readonly passConfirm = el('input', 'authmodal-input') as HTMLInputElement;
  private readonly email = el('input', 'authmodal-input') as HTMLInputElement;
  private readonly forgotBtn = el('button', 'authmodal-forgot');
  private readonly submitBtn = el('button', 'ui-btn ui-btn--primary ui-btn--sm ui-btn--block');
  private readonly msg = el('div', 'authmodal-msg');
  private mode: Mode = 'login';

  constructor(root: HTMLElement, private cb: AuthModalCallbacks) {
    const header = el('div', 'review-header');
    const title = el('div', 'review-title');
    tText(title, 'authmodal.title');
    const close = el('button', 'review-close');
    tText(close, 'mainmenu.back');
    close.onclick = () => {
      this.hide();
      this.cb.onClose();
    };
    header.append(title, close);

    const tabs = el('div', 'authmodal-tabs');
    tText(this.tabLogin, 'lobby.login');
    this.tabLogin.onclick = () => this.setMode('login');
    tText(this.tabSignup, 'lobby.signup');
    this.tabSignup.onclick = () => this.setMode('signup');
    tabs.append(this.tabLogin, this.tabSignup);

    this.user.type = 'text';
    this.user.maxLength = 20;
    this.user.placeholder = t('lobby.nickname');
    this.pass.type = 'password';
    this.pass.maxLength = 64;
    this.pass.placeholder = t('lobby.password');
    this.passConfirm.type = 'password';
    this.passConfirm.maxLength = 64;
    this.passConfirm.placeholder = t('lobby.passwordConfirm');
    this.email.type = 'email';
    this.email.maxLength = 80;
    this.email.placeholder = t('lobby.email');
    for (const inp of [this.user, this.pass, this.passConfirm, this.email]) inp.onkeydown = (e) => e.stopPropagation();
    this.pass.onkeydown = (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') this.submit();
    };

    tText(this.forgotBtn, 'lobby.forgotPassword');
    this.forgotBtn.onclick = () => {
      this.msg.textContent = t('lobby.forgotPasswordHint');
    };

    this.submitBtn.onclick = () => this.submit();

    this.modal.append(header, tabs, this.user, this.pass, this.passConfirm, this.email, this.forgotBtn, this.submitBtn, this.msg);
    this.overlay.append(this.modal);
    this.overlay.addEventListener('pointerdown', (e) => {
      if (e.target === this.overlay) {
        this.hide();
        this.cb.onClose();
      }
    });
    root.appendChild(this.overlay);
    this.renderMode();
  }

  private setMode(mode: Mode): void {
    this.mode = mode;
    this.msg.textContent = '';
    this.renderMode();
  }

  private renderMode(): void {
    const isSignup = this.mode === 'signup';
    this.tabLogin.classList.toggle('on', !isSignup);
    this.tabSignup.classList.toggle('on', isSignup);
    this.passConfirm.style.display = isSignup ? '' : 'none';
    this.email.style.display = isSignup ? '' : 'none';
    this.forgotBtn.style.display = isSignup ? 'none' : '';
    tText(this.submitBtn, isSignup ? 'lobby.signup' : 'lobby.login');
  }

  private submit(): void {
    const u = this.user.value.trim();
    const p = this.pass.value;
    if (!u || !p) {
      this.msg.textContent = t('lobby.authNeedBoth');
      return;
    }
    if (this.mode === 'signup') {
      if (p !== this.passConfirm.value) {
        this.msg.textContent = t('lobby.passwordMismatch');
        return;
      }
      this.msg.textContent = '';
      this.cb.onSignup(u, p, this.email.value.trim());
    } else {
      this.msg.textContent = '';
      this.cb.onLogin(u, p);
    }
  }

  get isOpen(): boolean {
    return this.overlay.classList.contains('open');
  }

  open(): void {
    this.setMode('login');
    this.user.value = '';
    this.pass.value = '';
    this.passConfirm.value = '';
    this.email.value = '';
    this.overlay.classList.add('open');
    this.user.focus();
  }

  hide(): void {
    this.overlay.classList.remove('open');
  }

  /** Login/signup went through — nothing more to show, close it. */
  onSuccess(): void {
    this.hide();
  }

  setError(message: string): void {
    this.msg.textContent = message;
  }
}
